import { useState } from "react";
import { Link } from "react-router-dom";
import type { ArticleItem, VideoItem } from "@/types/content";
import { formatDate, formatDuration, formatViews } from "@/utils/content";

interface HotSectionProps {
  videos: VideoItem[];
  articles: ArticleItem[];
}

type TabKey = "videos" | "articles";

export default function HotSection({ videos, articles }: HotSectionProps) {
  const [tab, setTab] = useState<TabKey>("videos");

  const videoList = [...videos].sort((a, b) => b.heat - a.heat);
  const articleList = [...articles].sort((a, b) => b.heat - a.heat);

  const featuredVideo = videoList[0];
  const featuredArticle = articleList[0];
  const featured = tab === "videos" ? featuredVideo : featuredArticle;
  const rest = tab === "videos" ? videoList.slice(1, 5) : articleList.slice(1, 5);

  const featuredLink = featured
    ? tab === "videos"
      ? `/videos/detail/${featured.id}`
      : `/articles/detail/${featured.id}`
    : "#";

  return (
    <div>
      <div className="mb-5 flex items-center gap-2">
        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          <button
            type="button"
            onClick={() => setTab("videos")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
              tab === "videos"
                ? "bg-background-50 font-medium text-foreground-950"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            热门视频
          </button>
          <button
            type="button"
            onClick={() => setTab("articles")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
              tab === "articles"
                ? "bg-background-50 font-medium text-foreground-950"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            热门文章
          </button>
        </div>
        <span className="hidden text-xs text-foreground-500 sm:block">按站内热度排序 · 演示数据</span>
      </div>

      {featured ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <Link
            to={featuredLink}
            className="group relative col-span-1 overflow-hidden rounded-lg bg-foreground-950 lg:col-span-7"
          >
            <div className="h-[260px] w-full sm:h-[320px] lg:h-[380px]">
              <img
                src={featured.cover}
                alt={`${featured.title}的封面图`}
                title={`${featured.title} - 热门推荐`}
                className="h-full w-full object-cover object-top opacity-90 transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
              <span className="inline-flex items-center gap-1 rounded-md bg-accent-500 px-2 py-1 text-[11px] font-medium text-background-50">
                <i className="ri-fire-line text-xs"></i>
                热度 {featured.heat}
              </span>
              <h3 className="mt-3 line-clamp-2-custom font-heading text-xl leading-snug text-background-50 md:text-2xl">
                {featured.title}
              </h3>
              <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-background-200/80">
                <span className="whitespace-nowrap">{featured.author}</span>
                <span className="whitespace-nowrap">{formatDate(featured.date)}</span>
                {tab === "videos" && "duration" in featured && (
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-time-line"></i>
                    {formatDuration(featured.duration)}
                  </span>
                )}
                {tab === "articles" && "readMinutes" in featured && (
                  <span className="whitespace-nowrap">{featured.readMinutes} 分钟阅读</span>
                )}
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-eye-line"></i>
                  {formatViews(featured.views)}
                </span>
              </p>
            </div>
          </Link>

          <div className="col-span-1 flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-2 lg:col-span-5">
            {rest.map((item, index) => {
              const link =
                tab === "videos"
                  ? `/videos/detail/${item.id}`
                  : `/articles/detail/${item.id}`;
              return (
                <Link
                  key={item.id}
                  to={link}
                  className="group flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-background-100"
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-medium ${
                      index === 0
                        ? "bg-primary-500 text-foreground-950"
                        : "bg-background-100 text-foreground-500"
                    }`}
                  >
                    {index + 2}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2-custom text-sm leading-snug text-foreground-900">
                      {item.title}
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-[11px] text-foreground-500">
                      <span className="whitespace-nowrap">{formatDate(item.date)}</span>
                      <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
                      <span className="whitespace-nowrap">热度 {item.heat}</span>
                    </p>
                  </div>
                  <i className="ri-arrow-right-s-line shrink-0 text-lg text-foreground-300 transition-colors group-hover:text-accent-500"></i>
                </Link>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-background-300 py-14 text-center text-sm text-foreground-500">
          暂无最新内容，敬请期待
        </p>
      )}
    </div>
  );
}